import React from "react";
import {Alert, Box, Button, List, ListItem, ListItemText, Tooltip, Typography} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import {useQuery} from "@tanstack/react-query";
import {queryKeys} from "../../data/queryKeys";
import {useDeleteTask, useRestoreTask} from "../../data/tasks";
import dayjs from "dayjs";
import {getArchivedTasksQuery} from "../../services/taskService";
import {ArchivedTask} from "../../types/task";
import {SettingsCard} from "../layouts/SettingsCard";

type ArchivedTasksViewProps = {
    boardId: string;
    sections: { id: string, title: string }[];
    archiveAfterDays?: number;
};

const describeRule = (days?: number) => {
    if (days === undefined) return null;
    if (days === 0) return 'Completed tasks are never archived automatically.';
    return `Completed tasks are archived automatically ${days} ${days === 1 ? 'day' : 'days'} after they're completed.`;
};

export function ArchivedTasksView({boardId, sections, archiveAfterDays}: ArchivedTasksViewProps) {
    const {data: archivedTasks = [], isLoading, isError} = useQuery({
        queryKey: queryKeys.archivedTasks(boardId),
        queryFn: () => getArchivedTasksQuery(boardId),
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const {mutate: restoreTask, isPending: isRestoring} = useRestoreTask(boardId);
    const {mutate: deleteTask, isPending: isDeleting} = useDeleteTask(boardId);

    const sectionTitle = (sectionId: string) => sections.find((s) => s.id === sectionId)?.title;

    const details = (task: ArchivedTask) => [
        task.date ? dayjs(task.date).format('ddd, MMM D · h:mm A') : null,
        sectionTitle(task.sectionId),
        task.completedAt ? `completed ${dayjs(task.completedAt).format('MMM D')}` : null,
        `archived ${dayjs(task.archivedAt).format('MMM D')}`,
    ].filter(Boolean).join(' · ');

    return (
        <SettingsCard
            title="Archived tasks"
            description={`Archived tasks are hidden from the board, week view, kiosk and search. ${describeRule(archiveAfterDays) ?? ''} Restoring a completed task restarts its archive timer.`}
        >
            {isError && <Alert severity="error">Couldn't load archived tasks.</Alert>}
            {!isLoading && !isError && archivedTasks.length === 0 && (
                <Typography color="text.secondary">No archived tasks.</Typography>
            )}

            <List disablePadding>
                {archivedTasks.map((task) => (
                    <ListItem
                        key={task.id}
                        divider
                        sx={{px: 0, '&:last-of-type': {borderBottom: 0}}}
                        secondaryAction={
                            <Box sx={{display: 'flex', gap: 0.5}}>
                                <Button
                                    size="small"
                                    startIcon={<UnarchiveIcon/>}
                                    disabled={isRestoring}
                                    onClick={() => restoreTask(task.id)}
                                >
                                    Restore
                                </Button>
                                <Tooltip title="Delete permanently">
                                    <IconButton
                                        size="small"
                                        aria-label="Delete archived task"
                                        disabled={isDeleting}
                                        onClick={() => deleteTask({id: task.id, sectionId: task.sectionId})}
                                    >
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    >
                        <ListItemText primary={task.title || 'Untitled'} secondary={details(task)} sx={{pr: 18}}/>
                    </ListItem>
                ))}
            </List>
        </SettingsCard>
    );
}
